/* eslint-disable react-refresh/only-export-components -- the reaction component + its trigger hook are intentionally colocated. */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@kidshome/ui';
import { useHero, useHeroCurrencyName, type EffortKind } from '@/entities/hero';

/**
 * A TRANSIENT "+N до зв'язку" bond reaction (ADR-0095 hero overhaul) — the REAL, event-driven feedback
 * for a bond gain. It is NOT decoration: it is rendered ONLY in response to a true effort completion,
 * shows the SERVED per-kind amount (`useHero().effortRates`, never a local mirror), animates
 * (pop → float → fade) and then disappears. After it fires, the hero's bond ring/level visibly advance
 * via the live query refresh.
 *
 * Reduced-motion-safe: the kit stills the animation, and {@link useBondReaction} clears it on a timer so
 * it auto-hides either way.
 */
export function BondReaction({
  amount,
  onDone,
  className,
}: {
  amount: number;
  onDone?: () => void;
  className?: string;
}): React.ReactElement {
  const { t } = useTranslation('play');
  // The reaction is always for the ACTIVE hero — resolve its skinned currency name (ADR-0103) here so both
  // render paths (the /hero scene + the imperative useBondReaction) show "+N stars" / "+N credits".
  const { hero } = useHero();
  const currency = useHeroCurrencyName(hero?.archetypeId ?? null);
  useEffect(() => {
    if (!onDone) return;
    const id = setTimeout(onDone, 2600);
    return () => clearTimeout(id);
  }, [onDone]);
  return (
    <span className={cn('reaction', className)} role="status">
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 21s-7.5-4.6-7.5-10A4.5 4.5 0 0 1 12 7a4.5 4.5 0 0 1 7.5 4c0 5.4-7.5 10-7.5 10z"
          fill="currentColor"
        />
      </svg>
      {t('hero.level.reaction', { n: amount, currency })}
    </span>
  );
}

/**
 * Imperative bond-reaction trigger. `fire(kind)` raises a transient reaction for that effort's real bond
 * amount; `reaction` is the node to drop into a positioned slot on the action surface (or `null`). Call
 * `fire` from a completion's success — never render the reaction unconditionally.
 */
export function useBondReaction(opts?: { enabled?: boolean }): {
  reaction: React.ReactNode;
  fire: (kind: EffortKind) => void;
} {
  // The SERVED award table (deduped query — the surface already reads the hero). Falls back to the
  // local mirror only while unresolved, so an offline completion still gets its honest "+N".
  // The bond reaction only ever fires for the KID assignee, so callers on a shared surface (e.g. the
  // guardian-reachable chore detail) pass `enabled: isKid` — a guardian holds neither play:read nor
  // cosmetics:read, so an ungated read here would 403 on `/play/hero` + `/cosmetics/equipped`.
  const { effortRates } = useHero({ enabled: opts?.enabled });
  const [state, setState] = useState<{ amount: number; key: number } | null>(null);
  const fire = useCallback(
    (kind: EffortKind) => {
      // `key` (re-)mounts the reaction so it re-animates on every gain, even back-to-back.
      setState({ amount: effortRates[kind], key: Date.now() });
    },
    [effortRates],
  );
  const reaction = state ? (
    <BondReaction key={state.key} amount={state.amount} onDone={() => setState(null)} />
  ) : null;
  return { reaction, fire };
}
